import { createModel } from '@rematch/core';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { Member } from './members';
import { Controller } from './controllers';
import { Contract } from './contracts';
import { Cycle } from './cycles';
import { AxiosError } from 'axios';
import { UpdateOrganizationBody } from '../../pages/OrganizationsView/types';
import CycleMeasurement from '../../enums/cycle-measurement';

export type Organization = {
  id: string;
  name: string;
  logo: string | null;
  active: boolean;
  created_at: Date;
  updated_at: Date;
  members?: Member[];
  locations?: Location[];
  controllers?: Controller[];
  contracts?: Contract[];
  cycles?: Cycle[];
  isCelsius: boolean;
  isTecheat: boolean;
};

const DEFAULT_STATE: { organization: Organization | null } = {
  organization: null,
};

export const organizations = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    setOne(state, payload) {
      return {
        ...state,
        organization: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        organization: DEFAULT_STATE.organization,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getOrganizationUnits() {
        try {
          const { organization } = await dispatch.organizations.getOrganization();

          const units = organization.isCelsius ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit;

          return units;
        } catch (error: unknown) {
          console.error(error);
          return CycleMeasurement.Fahrenheit;
        }
      },

      async getOrganization() {
        try {
          const response = await http<Organization>(URL.GET_CURRENT_ORGANIZATION);

          dispatch.organizations.setOne(response.data);
          return {
            organization: response.data,
          };
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateOrganization(formData: UpdateOrganizationBody) {
        try {
          const response = await http.put(URL.UPDATE_CURRENT_ORGANIZATION, formData);
          dispatch.organizations.setOne(response.data);
          toast.success('Organization updated successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async uploadLogo(file) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          await http.post(URL.UPLOAD_ORGANIZATION_LOGO, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async deleteLogo() {
        try {
          await http.delete(URL.DELETE_ORGANIZATION_LOGO);
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
    };
  },
});
